import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Video, Mic, StopCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function MeetingRoom() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [stream, setStream] = useState(null);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notes, setNotes] = useState(null);
  const [segments, setSegments] = useState([]);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef(null);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    fetch(`${API}/meeting/${token}`).then(r => r.json()).then(setInfo).catch(() => toast.error("Meeting not found"));
  }, [token]);

  useEffect(() => {
    if (!recording) return;
    const t = setInterval(() => setDuration(d => d + 1), 1000);
    return () => clearInterval(t);
  }, [recording]);

  const startCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setStream(s);
      if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.muted = true; }
    } catch { toast.error("Camera/mic permission denied"); }
  };

  const startRecording = () => {
    if (!stream) return;
    chunksRef.current = [];
    const mr = new MediaRecorder(stream, { mimeType: "video/webm" });
    mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    mr.onstop = async () => {
      setUploading(true);
      try {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        const fd = new FormData();
        fd.append("file", blob, `meeting-${token}.webm`);
        const resp = await fetch(`${API}/meeting/${token}/upload`, { method: "POST", body: fd });
        if (!resp.ok) throw new Error("upload");
        const data = await resp.json();
        setNotes(data.notes);
        setSegments(data.transcript_segments || []);
        toast.success("Meeting saved — AI notes ready");
      } catch { toast.error("Upload failed"); }
      finally { setUploading(false); stream.getTracks().forEach(t => t.stop()); setStream(null); }
    };
    mr.start();
    mrRef.current = mr;
    setRecording(true);
    setDuration(0);
  };

  const stopRecording = () => {
    if (mrRef.current) mrRef.current.stop();
    setRecording(false);
  };

  const fmtDuration = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  if (!info) return <div className="min-h-screen grid place-items-center">Loading meeting…</div>;

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="meeting-room">
      <div className="hero-gradient relative">
        <div className="grain" />
        <div className="relative max-w-5xl mx-auto px-6 py-6 flex items-center gap-3 text-white">
          <div className="w-8 h-8 rounded-md grid place-items-center bg-pink"><Sparkles className="w-4 h-4 text-white" /></div>
          <div>
            <div className="font-bold">Alfred Meeting Room</div>
            <div className="text-xs text-white/70">AI notetaker active · your meeting is transcribed and summarised on save</div>
          </div>
          {recording && (
            <div className="ml-auto flex items-center gap-2 rounded-full bg-red-500/20 border border-red-400 px-3 py-1">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-xs font-mono">REC · {fmtDuration(duration)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 -mt-4 pb-16 space-y-4">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-pink font-semibold">Interview</div>
                <h1 className="text-2xl font-bold mt-1">{info.candidate_name}</h1>
                <div className="text-sm text-neutral-500 mt-1">
                  {info.scheduled_at && new Date(info.scheduled_at).toLocaleString()} · {info.duration_minutes} min · with {info.interviewer_name}
                </div>
              </div>
              <Badge className={info.status === "COMPLETED" ? "bg-teal text-white border-0" : ""}>{info.status}</Badge>
            </div>

            {!notes && !info.has_recording && (
              <>
                <video ref={videoRef} autoPlay playsInline className="w-full rounded-lg bg-black aspect-video" data-testid="meeting-video" />
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {!stream && <Button className="btn-primary rounded-full" onClick={startCamera} data-testid="meeting-start-camera"><Video className="w-4 h-4 mr-1" /> Start camera</Button>}
                  {stream && !recording && <Button className="btn-primary rounded-full" onClick={startRecording} data-testid="meeting-start-rec"><Mic className="w-4 h-4 mr-1" /> Start recording</Button>}
                  {recording && <Button variant="destructive" className="rounded-full" onClick={stopRecording} disabled={uploading} data-testid="meeting-stop-rec"><StopCircle className="w-4 h-4 mr-1" /> Stop & save</Button>}
                  {uploading && <span className="text-sm text-neutral-500">Uploading and generating AI notes…</span>}
                </div>
              </>
            )}

            {notes && (
              <div className="space-y-4" data-testid="meeting-notes">
                <div className="flex items-center gap-2 text-teal"><CheckCircle2 className="w-5 h-5" /><span className="font-semibold">Meeting saved · AI notes ready</span></div>
                <NotesView notes={notes} />
                {segments.length > 0 && <TranscriptChapters segments={segments} />}
              </div>
            )}

            {info.has_recording && !notes && (
              <div className="rounded-lg bg-neutral-100 p-8 text-center text-neutral-600">
                This meeting has already been recorded and analysed. Open the candidate profile in the workspace to see the notes.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function NotesView({ notes }) {  if (!notes) return null;
  return (
    <div className="space-y-3 text-sm">
      {notes.summary && (
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Executive summary</div>
          <div className="text-neutral-800">{notes.summary}</div>
        </div>
      )}
      {notes.recommended_next_step && (
        <div className="rounded-lg border p-3">
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Recommended next step</div>
          <Badge className="bg-pink text-white border-0">{String(notes.recommended_next_step).replace(/_/g, " ")}</Badge>
          {notes.confidence != null && <span className="ml-2 text-xs text-neutral-500">Confidence: {Math.round((notes.confidence || 0) * 100)}%</span>}
        </div>
      )}
      {Array.isArray(notes.strengths) && notes.strengths.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-teal mb-1">Strengths</div>
          <ul className="list-disc ml-5 space-y-1 text-neutral-800">{notes.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      )}
      {Array.isArray(notes.key_highlights) && notes.key_highlights.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Key highlights</div>
          <ul className="list-disc ml-5 space-y-1 text-neutral-800">{notes.key_highlights.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      )}
      {Array.isArray(notes.red_flags) && notes.red_flags.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-red-500 mb-1">Red flags</div>
          <ul className="list-disc ml-5 space-y-1 text-neutral-800">{notes.red_flags.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      )}
      {notes.star_analysis && (
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">STAR analysis</div>
          <div className="text-neutral-800">{notes.star_analysis}</div>
        </div>
      )}
    </div>
  );
}

/**
 * TranscriptChapters — groups Whisper segments into ~60-second chapters (proxy for speaker turns
 * since Whisper doesn't diarise) with clickable jump-tabs. Selecting a chapter reveals its segments
 * and scrolls the transcript row into view + pulses briefly.
 */
function TranscriptChapters({ segments }) {
  const [selected, setSelected] = useState(0);
  const [flash, setFlash] = useState(null);
  const rowRefs = useRef({});
  const CHAPTER_LEN = 60; // seconds

  // Build chapter buckets keyed by start-of-window (0s, 60s, 120s, …)
  const chapters = [];
  segments.forEach(s => {
    const bucket = Math.floor((s.start || 0) / CHAPTER_LEN);
    if (!chapters[bucket]) {
      chapters[bucket] = { idx: bucket, start: bucket * CHAPTER_LEN, segments: [] };
    }
    chapters[bucket].segments.push(s);
  });
  const compact = chapters.filter(Boolean).map((c, i) => ({ ...c, i, preview: (c.segments[0]?.text || "").trim().slice(0, 60) }));

  const jumpTo = (i) => {
    setSelected(i);
    setFlash(compact[i].start);
    setTimeout(() => setFlash(null), 1400);
    const first = compact[i].segments[0]?.start;
    const el = rowRefs.current[first];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const fmt = (s) => `${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,"0")}`;

  return (
    <div className="border-t pt-4 space-y-3" data-testid="meeting-transcript-chapters">
      <div className="text-xs uppercase tracking-widest text-neutral-500 flex items-center gap-2">
        <Sparkles className="w-3 h-3 text-pink" /> Transcript · {compact.length} chapter{compact.length === 1 ? "" : "s"}
      </div>
      <div className="flex gap-1 flex-wrap" data-testid="meeting-chapter-tabs">
        {compact.map(c => (
          <button
            key={c.i}
            onClick={() => jumpTo(c.i)}
            data-testid={`meeting-chapter-${c.i}`}
            title={c.preview}
            className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5 transition-colors ${selected===c.i ? "bg-pink text-white border-pink font-semibold" : "border-neutral-200 hover:border-pink hover:bg-pink/5"}`}>
            <span className="font-mono text-[10px]">{fmt(c.start)}</span>
            <span className="hidden md:inline truncate max-w-[180px]">{c.preview || `Chapter ${c.i+1}`}</span>
          </button>
        ))}
      </div>
      <div className="rounded-lg border border-neutral-200 bg-white max-h-72 overflow-y-auto p-1" data-testid="meeting-transcript-body">
        {segments.map((s, i) => (
          <div
            key={i}
            ref={el => (rowRefs.current[s.start] = el)}
            className={`flex gap-3 px-3 py-2 text-sm rounded-md transition-colors ${flash != null && Math.floor((s.start||0)/CHAPTER_LEN) === Math.floor(flash/CHAPTER_LEN) ? "bg-pink/10" : "hover:bg-neutral-50"}`}>
            <span className="text-[10px] font-mono text-neutral-400 shrink-0 pt-0.5 w-10">{fmt(s.start || 0)}</span>
            <span className="text-neutral-700 leading-relaxed">{s.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
